import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api, { errMsg, fileUrl } from "@/lib/api";
import { Loading, ErrorState, Avatar } from "@/components/common";
import Voltar from "@/components/Voltar";
import { useAuth } from "@/context/AuthContext";
import { useCurrentDeal } from "@/context/CurrentDealContext";
import { brl, fmtDate, fmtDateTime, fmtDataPrevista, stageColor, stageLabel, MANDALA } from "@/lib/format";
import { extractDateFromText, isSameLocalDay } from "@/lib/dateFromText";
import ReagendarProximaDialog from "@/components/ReagendarProximaDialog";
import DivergenciaDataDialog from "@/components/DivergenciaDataDialog";
import HelpTip from "@/components/HelpTip";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import LikeButton from "@/components/LikeButton";
import RegistrarAtividade from "@/components/RegistrarAtividade";
import CobrarWhatsApp from "@/components/CobrarWhatsApp";
import GanhoModal from "@/components/GanhoModal";
import CelebrationOverlay from "@/components/CelebrationOverlay";
import {
  ArrowLeft, Pencil, Trash2, Send, Upload, Clock, MessageSquare, GitBranch, Image as ImageIcon,
  Loader2, Target, CheckCircle2, XCircle, CalendarPlus, Phone, MapPin, AlertTriangle, ChevronRight, Check, Megaphone, ClipboardList, MoreHorizontal, Plus, RefreshCw,
} from "@/components/icons";

const OPEN_STAGES = ["Prospecção", "Qualificação", "Testes", "Proposta", "Negociação"];
const INPUT = "w-full h-11 px-3.5 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40";
const EVENT_ICON = { created: Target, stage: GitBranch, lost: GitBranch, comment: MessageSquare, cobranca: Megaphone, resposta_cobranca: MessageSquare, feedback: MessageSquare, field: Pencil, activity: Clock, photo: ImageIcon };
const MOTIVOS_PERDA = ["Preço", "Concorrente", "Sem orçamento", "Sem retorno do cliente", "Produto não atende", "Outro"];

export default function DealDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setDeal: setDealGlobal, clear: clearDealGlobal } = useCurrentDeal();
  const [deal, setDeal] = useState(null);
  const [history, setHistory] = useState(null);
  const [comments, setComments] = useState([]);
  const [fotos, setFotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // LOTE UX-1 A5 — status do cliente (buscado após a deal) para esconder o
  // toggle "Marcar como ampliação de mix" quando não for Ativo.
  const [clienteStatus, setClienteStatus] = useState(null);
  // LOTE UX-1 A3 — bottom-sheet "Mudar etapa" (ações rápidas mobile+desktop).
  const [mudarEtapaOpen, setMudarEtapaOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [uploading, setUploading] = useState(false);
  const [ativOpen, setAtivOpen] = useState(false);
  const [registrarOpen, setRegistrarOpen] = useState(null);
  const [faltaOpen, setFaltaOpen] = useState(null); // F1 — "Para ir até <alvo> falta"
  const [proximo, setProximo] = useState(null); // { stage_atual, proxima, itens[] }
  const [voltarOpen, setVoltarOpen] = useState(false);
  const [voltarMotivo, setVoltarMotivo] = useState("");
  const [voltarBusy, setVoltarBusy] = useState(false); // null | { deal, concluir }
  const [pularOpen, setPularOpen] = useState(false);
  const [pularStage, setPularStage] = useState("");
  const [pularMotivo, setPularMotivo] = useState("");
  const [pularBusy, setPularBusy] = useState(false);
  const [ativForm, setAtivForm] = useState({ tipo: "Próximo contato", titulo: "", data: "" });
  const [savingAtiv, setSavingAtiv] = useState(false);
  // LOTE LT-1b — exclusão admin com motivo obrigatório.
  const [adminDelOpen, setAdminDelOpen] = useState(false);
  const [adminDelMotivo, setAdminDelMotivo] = useState("");
  const [adminDelBusy, setAdminDelBusy] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const [deals, hist, cmts, fts, prox] = await Promise.all([
        api.get("/deals"), api.get(`/deals/${id}/history`), api.get(`/deals/${id}/comments`), api.get(`/fotos/${id}`),
        api.get(`/deals/${id}/proximo-passo`).catch(() => ({ data: null })),
      ]);
      const d = (deals.data || []).find((x) => x.id === id);
      if (!d) { setError(true); return; }
      setDeal(d); setHistory(hist.data); setComments(cmts.data || []); setFotos(fts.data || []);
      setProximo(prox.data || null);
    } catch { setError(true); } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // LOTE UX-1 A1 — expõe a deal no contexto global para o "+" da barra inferior.
  useEffect(() => {
    if (deal) setDealGlobal(deal);
    return () => clearDealGlobal();
  }, [deal, setDealGlobal, clearDealGlobal]);

  // LOTE UX-1 A5 — busca o status do cliente para decidir se o toggle
  // "Marcar como ampliação de mix" pode aparecer.
  useEffect(() => {
    const cid = deal?.cliente_id;
    if (!cid) { setClienteStatus(null); return; }
    api.get(`/clientes/${cid}`).then((r) => setClienteStatus(r.data?.status || null)).catch(() => setClienteStatus(null));
  }, [deal?.cliente_id]);

  const voltarEtapa = async () => {
    if (!voltarMotivo.trim() || voltarMotivo.trim().length < 3) {
      toast.error("Escreva o motivo para voltar de etapa."); return;
    }
    setVoltarBusy(true);
    try {
      const { data } = await api.post(`/deals/${id}/voltar-etapa`, { motivo: voltarMotivo.trim() });
      toast.success(`Voltou para ${data.to_stage}`);
      setVoltarOpen(false); setVoltarMotivo("");
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setVoltarBusy(false); }
  };

  const abrirPular = () => {
    // sugere a próxima etapa à frente como default
    const idx = OPEN_STAGES.indexOf(deal?.stage);
    setPularStage(idx >= 0 && idx < OPEN_STAGES.length - 2 ? OPEN_STAGES[idx + 2] : "");
    setPularMotivo("");
    setPularOpen(true);
  };
  const pularEtapa = async () => {
    if (!pularStage) { toast.error("Escolha a etapa de destino."); return; }
    if (pularMotivo.trim().length < 3) { toast.error("Escreva o motivo (mínimo 3 caracteres)."); return; }
    setPularBusy(true);
    try {
      const { data } = await api.post(`/deals/${id}/pular-etapa`, { to_stage: pularStage, motivo: pularMotivo.trim() });
      toast.success(`Pulou para ${data.to_stage}`);
      setPularOpen(false); setPularMotivo(""); setPularStage("");
      load();
    } catch (err) {
      const dt = err?.response?.data?.detail;
      if (dt?.erro === "prospeccao_incompleta") {
        toast.error(dt.mensagem || "Complete os dados para pular a etapa.");
      } else toast.error(errMsg(err));
    } finally { setPularBusy(false); }
  };

  const changeStage = async (stage) => {
    if (stage === deal.stage) return;
    try {
      await api.patch(`/deals/${id}/stage`, { stage });
      toast.success(`Etapa: ${stage}`);
      load();
    } catch (err) {
      const dt = err?.response?.data?.detail;
      if (dt?.erro === "prospeccao_incompleta") {
        toast.error(dt.mensagem || "Complete os dados da oportunidade para avançar.", {
          action: { label: "Editar", onClick: () => navigate(`/deal/${id}/editar`) },
        });
      } else if (dt?.erro === "cnpj_obrigatorio") {
        toast.error("Informe o CNPJ do Cliente para avançar.", {
          action: { label: "Editar", onClick: () => navigate(`/cliente/${dt.cliente_id}/editar`) },
        });
      } else toast.error(errMsg(err));
    }
  };

  const marcarGanho = async () => {
    setGanhoOpen(true);
  };

  // LOTE G1 — estado do novo modal "Fechar como GANHO" + overlay de parabéns.
  const [ganhoOpen, setGanhoOpen] = useState(false);
  const [ganhoBusy, setGanhoBusy] = useState(false);
  const [celebrateOpen, setCelebrateOpen] = useState(false);
  const [celebrateInfo, setCelebrateInfo] = useState(null);

  const confirmarGanho = async ({ valor_fechado, data_fechamento }) => {
    setGanhoBusy(true);
    try {
      await api.patch(`/deals/${id}/stage`, { stage: "Fechado", valor_fechado, data_fechamento });
      setGanhoOpen(false);
      // Primeiro nome do usuário logado para a mensagem personalizada.
      const nome = String(user?.name || "").trim().split(/\s+/)[0];
      setCelebrateInfo({
        clienteNome: deal?.cliente_nome || "cliente",
        valorFechado: valor_fechado,
        primeiroNome: nome,
      });
      setCelebrateOpen(true);
      load();
    } catch (err) {
      const dt = err?.response?.data?.detail;
      if (dt?.erro === "prospeccao_incompleta") {
        toast.error(dt.mensagem || "Complete os dados da oportunidade para concluir.", {
          action: { label: "Editar", onClick: () => navigate(`/deal/${id}/editar`) },
        });
      } else if (dt?.erro === "cnpj_obrigatorio") {
        toast.error("Informe o CNPJ do Cliente para concluir.", {
          action: { label: "Editar", onClick: () => navigate(`/cliente/${dt.cliente_id}/editar`) },
        });
      } else if (dt?.erro === "ganho_muito_cedo") {
        toast.error(dt.mensagem || "Marcar Ganho só a partir de Proposta ou Negociação.");
      } else if (dt?.erro === "ganho_sem_valor") {
        toast.error(dt.mensagem || "Informe o valor mensal (> 0) para marcar Ganho.");
      } else toast.error(errMsg(err));
    } finally { setGanhoBusy(false); }
  };

  const [perdaOpen, setPerdaOpen] = useState(false);
  const [perdaMotivo, setPerdaMotivo] = useState(""); // começa vazio — item 6 do pedido
  const [perdaOutro, setPerdaOutro] = useState("");
  // LOTE AJ-2 E2 — janela de perda ganha "Retorno em N dias" + "Não criar retorno".
  const [perdaDiasRetorno, setPerdaDiasRetorno] = useState(90);
  const [perdaNaoCriarRetorno, setPerdaNaoCriarRetorno] = useState(false);
  const [stageHistory, setStageHistory] = useState([]);

  // LOTE AJ-2 E1 — modal de "Reabrir" oportunidade Ganha/Perdida.
  const [reabrirOpen, setReabrirOpen] = useState(false);
  const [reabrirMotivo, setReabrirMotivo] = useState("");

  const abrirPerdido = () => {
    setPerdaMotivo("");
    setPerdaOutro("");
    setPerdaDiasRetorno(90);
    setPerdaNaoCriarRetorno(false);
    setPerdaOpen(true);
  };

  const marcarPerdido = async () => {
    const motivo = perdaMotivo === "Outro" ? perdaOutro.trim() : perdaMotivo;
    if (!motivo) { toast.error("Informe o motivo da perda."); return; }
    try {
      const dias = Math.max(1, parseInt(perdaDiasRetorno, 10) || 90);
      await api.patch(`/deals/${id}/stage`, {
        stage: "Perdido",
        motivo_perda: motivo,
        dias_retorno: dias,
        nao_criar_retorno: !!perdaNaoCriarRetorno,
      });
      toast.success("Oportunidade marcada como Perdido");
      setPerdaOpen(false); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const abrirReabrir = () => {
    setReabrirMotivo("");
    setReabrirOpen(true);
  };

  const reabrirDeal = async () => {
    const m = reabrirMotivo.trim();
    if (m.length < 3) { toast.error("Informe o motivo da reabertura (mínimo 3 caracteres)."); return; }
    try {
      const r = await api.post(`/deals/${id}/reabrir`, { motivo: m });
      toast.success(`Reaberta para ${r?.data?.to_stage || "etapa anterior"}`);
      setReabrirOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  // Cobrança do gestor — modal unificado via CobrarWhatsApp
  // Responder cobrança (para o responsável)
  const [respCobrId, setRespCobrId] = useState(null);
  const [respText, setRespText] = useState("");
  const responderCobranca = async () => {
    if (!respText.trim() || !respCobrId) { toast.error("Escreva sua resposta."); return; }
    try {
      await api.post(`/deals/${id}/cobrancas/${respCobrId}/responder`, { text: respText.trim() });
      toast.success("Resposta enviada");
      setRespCobrId(null); setRespText(""); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const changeFase = async (fase) => {
    try { await api.patch(`/deals/${id}/fase`, { fase_mandala: fase ? Number(fase) : null }); toast.success("Metodologia Allia atualizada"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const openAtiv = (tipo, titulo = "") => { setAtivForm({ tipo, titulo, data: "" }); setAtivOpen(true); };

  // Ao registrar Contato/Visita, se já existir atividade pendente do mesmo
  // tipo para este cliente, apenas conclui essa (sem criar nova).
  const registrarInteracao = async (tipo, titulo = "") => {
    try {
      if (deal?.cliente_id) {
        const { data: pend } = await api.get(`/atividades`);
        const alvo = (pend || []).find(
          (a) => !a.concluida && a.tipo === tipo && a.cliente_id === deal.cliente_id
        );
        if (alvo) {
          await api.patch(`/atividades/${alvo.id}/toggle`);
          toast.success(`${tipo} registrada — atividade pendente concluída`);
          load();
          return;
        }
      }
      openAtiv(tipo, titulo);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const concluirProximaAcao = async () => {
    if (!deal?.next_action?.id) return;
    const aid = deal.next_action.id;
    setDeal((d) => (d ? { ...d, next_action: null } : d));
    try {
      await api.patch(`/atividades/${aid}/toggle`);
      toast.success("Próxima ação concluída");
      await load();
      if (deal?.next_action) toast.info("Outra atividade pendente virou a próxima ação.");
    } catch (err) { toast.error(errMsg(err)); await load(); }
  };

  const [reagOpen, setReagOpen] = useState(false);
  const [reagData, setReagData] = useState("");
  const [reagDiverg, setReagDiverg] = useState(null); // {texto, campo} ou null
  const [gestorNaOpen, setGestorNaOpen] = useState(false); // detalhes read-only p/ gestor
  const abrirReagendar = () => {
    if (!deal?.next_action?.data) return;
    const d = new Date(deal.next_action.data);
    const pad = (n) => String(n).padStart(2, "0");
    setReagData(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
    setReagOpen(true);
  };
  const _executarReagendar = async (novaData) => {
    try {
      await api.patch(`/atividades/${deal.next_action.id}`, { data: novaData.toISOString() });
      toast.success("Próxima ação reagendada");
      setReagOpen(false); setReagDiverg(null); load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const reagendarProximaAcao = async () => {
    if (!deal?.next_action?.id || !reagData) return;
    // Aviso de divergência contra o texto da atividade existente (descrição / título).
    const textoAtiv = `${deal.next_action.descricao || ""} ${deal.next_action.titulo || ""}`;
    const dTexto = extractDateFromText(textoAtiv);
    const dCampo = new Date(reagData);
    if (dTexto && !isSameLocalDay(dTexto, dCampo)) {
      setReagDiverg({ texto: dTexto, campo: dCampo });
      return;
    }
    await _executarReagendar(dCampo);
  };

  const salvarAtiv = async (e) => {
    e.preventDefault();
    setSavingAtiv(true);
    try {
      await api.post("/atividades", {
        titulo: ativForm.titulo || ativForm.tipo, tipo: ativForm.tipo,
        data: ativForm.data ? new Date(ativForm.data).toISOString() : new Date().toISOString(),
        deal_id: id, cliente_id: deal.cliente_id || null,
      });
      toast.success("Atividade registrada");
      setAtivOpen(false); load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSavingAtiv(false); }
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    try { await api.post(`/deals/${id}/comments`, { text: comment.trim(), mentions: [] }); setComment(""); const { data } = await api.get(`/deals/${id}/comments`); setComments(data || []); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", file); await api.post(`/upload?deal_id=${id}`, fd); toast.success("Foto anexada"); const { data } = await api.get(`/fotos/${id}`); setFotos(data || []); }
    catch (err) { toast.error(errMsg(err)); } finally { setUploading(false); e.target.value = ""; }
  };

  const del = async () => {
    if (!window.confirm("Excluir esta Oportunidade?")) return;
    try { await api.delete(`/deals/${id}`); toast.success("Excluída"); navigate("/pipeline"); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const adminDel = async () => {
    if (adminDelMotivo.trim().length < 3) return;
    setAdminDelBusy(true);
    try {
      await api.delete(`/admin/deals/${id}`, { params: { motivo: adminDelMotivo.trim() } });
      toast.success("Oportunidade movida para a lixeira");
      setAdminDelOpen(false);
      navigate("/pipeline");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setAdminDelBusy(false); }
  };

  if (loading) return <Loading />;
  if (error) return <ErrorState onRetry={load} message="Oportunidade não encontrada." />;

  const encerrada = deal.stage === "Fechado" || deal.stage === "Perdido";
  const na = deal.next_action;
  const spDate = (d) => new Date(d).toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" });
  const naOverdue = na?.data && spDate(new Date()) > spDate(na.data);
  // Bolinhas do stepper — item 5 do CORRECAO_EMERGENT_FLUXO_FUNIL.
  // Quando Perdido, mostra só as etapas por onde a oportunidade passou (from_stage
  // da transição que virou "Perdido") + a bolinha "Perdido" no fim.
  const stepperEtapas = (() => {
    if (deal.stage === "Perdido") {
      const lost = [...((history?.events) || [])].reverse().find((e) => e.type === "lost");
      const paradaEm = OPEN_STAGES.includes(lost?.from_stage) ? lost.from_stage : "Prospecção";
      const stopIdx = OPEN_STAGES.indexOf(paradaEm);
      return [...OPEN_STAGES.slice(0, stopIdx + 1), "Perdido"];
    }
    return [...OPEN_STAGES, "Fechado"];
  })();
  const stepIndex = stepperEtapas.indexOf(deal.stage);

  // LOTE F1 — bolinhas do stepper clicáveis. Pular à frente valida via
  // /pre-validar; voltar pede confirmação; Encerramento abre modal "Fechar
  // como GANHO" do G1. Encerrada (Fechado/Perdido) → bolinhas sem clique.
  const stepperClick = async (destino) => {
    if (encerrada || !isOwner) return;
    if (destino === deal.stage) return;
    const alvoIdx = stepperEtapas.indexOf(destino);
    const curIdx = stepIndex;
    if (destino === "Fechado") { setGanhoOpen(true); return; }
    if (alvoIdx < curIdx) {
      if (!window.confirm(`Voltar para ${destino}?`)) return;
      try { await api.patch(`/deals/${id}/stage`, { stage: destino }); toast.success(`Movido para ${destino}`); load(); }
      catch (err) { toast.error(errMsg(err)); }
      return;
    }
    // Avançar: pré-validar
    try {
      const r = await api.get(`/deals/${id}/pre-validar`, { params: { alvo: destino } });
      if (r.data?.ok) {
        await api.patch(`/deals/${id}/stage`, { stage: destino });
        toast.success(`Movido para ${destino}`);
        load();
      } else {
        setFaltaOpen({ alvo: destino, itens: r.data?.itens || [] });
      }
    } catch (err) {
      const dt = err?.response?.data?.detail;
      if (dt?.erro === "cnpj_obrigatorio") toast.error("Informe o CNPJ do Cliente.", { action: { label: "Editar", onClick: () => navigate(`/cliente/${dt.cliente_id}/editar`) } });
      else toast.error(errMsg(err));
    }
  };

  const isOwner = deal.owner_id === user?.id;
  const isManager = ["admin", "director", "manager", "diretor_geral"].includes(user?.role);
  // Modo gestão: gestor logado NÃO é o responsável — só leitura + cobrar/reatribuir.
  const gestao = !isOwner && isManager;
  const cobrancasAbertas = (comments || []).filter((c) => c.kind === "cobranca" && c.status !== "respondida");

  // LOTE T1 — texto do balão (HelpTip) de cada bolinha do stepper.
  const stageHistMap = (() => {
    const m = {};
    (history?.events || []).forEach((ev) => {
      if (ev.type === "stage" && ev.to_stage && !m[ev.to_stage]) m[ev.to_stage] = ev.created_at || ev.data;
    });
    if (!m[OPEN_STAGES[0]]) m[OPEN_STAGES[0]] = deal.created_at;
    return m;
  })();
  const daysBetween = (iso) => {
    try { return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)); } catch { return null; }
  };
  const stepperTip = (s, i) => {
    if (!isOwner && !gestao && user) return `Só ${(deal.owner_name || "o responsável").split(" ")[0]} muda a etapa.`;
    if (gestao && !isOwner) return `Só ${(deal.owner_name || "o responsável").split(" ")[0]} muda a etapa.`;
    const isGanho = s === "Fechado";
    const isPerdido = s === "Perdido";
    const active = i === stepIndex;
    const done = i < stepIndex;
    if (active) {
      const dias = daysBetween(stageHistMap[s] || deal.stage_updated_at || deal.created_at);
      return `Etapa atual · há ${dias ?? 0} ${dias === 1 ? "dia" : "dias"}`;
    }
    if (done) {
      const dt = stageHistMap[s];
      const dd = dt ? new Date(dt).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }) : "—";
      return `${s} · concluída em ${dd}`;
    }
    // à frente
    if (isGanho) return "Fechar como ganho";
    if (isPerdido) return `Avançar para ${s}`;
    // próxima imediata? usar `proximo.itens` quando s === proximo.proxima
    const alvoDireto = (proximo?.proxima && proximo.proxima === s) ? proximo : null;
    if (alvoDireto) {
      const itens = (alvoDireto.itens || []).filter((it) => !it.ok);
      if (itens.length === 0) return `Avançar para ${s}\nTudo pronto`;
      return `Avançar para ${s}\nFalta: ${itens.map((it) => it.texto || it.chave).slice(0, 3).join(", ")}`;
    }
    if (s === "Fechado" || s === "Encerramento") return "Fechar como ganho";
    return `Avançar para ${s}\nValidar ao clicar`;
  };

  // etapa anterior (quando a atual permite voltar): sobrescreve done com "Voltar para ...".
  const stepperTipFinal = (s, i) => {
    if (encerrada) return stepperTip(s, i);
    if (i < stepIndex) return `Voltar para ${s}`;
    return stepperTip(s, i);
  };

  return (
    <div className="max-w-4xl">
      <Voltar fallback="/pipeline" label="Voltar" className="mb-4" />

      {/* Cabeçalho */}
      <div className="bg-card rounded-2xl border border-black/5 p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${stageColor(deal.stage)}`}>{stageLabel(deal.stage)}</span>
              {deal.cross_selling ? (
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-brand-soft text-brand-hover" data-testid="cross-selling-selo" title="Oportunidade de ampliação de mix (cliente com faturamento nos últimos 12 meses)">Ampliação de mix</span>
              ) : null}
              {deal.owner_id === user?.id && (clienteStatus === "Ativo" || deal.cross_selling) && (
                <button
                  onClick={async () => {
                    try {
                      const { data: r } = await api.patch(`/deals/${id}/cross-selling`, { cross_selling: !deal.cross_selling });
                      setDeal((d) => ({ ...d, cross_selling: r.cross_selling }));
                      toast.success(r.cross_selling ? "Marcado como ampliação de mix" : "Marca de ampliação de mix removida");
                    } catch (e) { toast.error(errMsg(e) || "Falha ao alternar"); }
                  }}
                  data-testid="cross-selling-toggle"
                  className="text-[11px] text-brand hover:underline"
                >
                  {deal.cross_selling ? "Não é ampliação de mix" : "Marcar como ampliação de mix"}
                </button>
              )}
            </div>
            <h1 className="text-2xl font-semibold text-navy mt-2">{deal.titulo}</h1>
            <button onClick={() => deal.cliente_id && navigate(`/cliente/${deal.cliente_id}`)} className="text-sm text-brand hover:underline mt-0.5">{deal.cliente_nome || "Sem cliente"}</button>
          </div>
          <div className="w-full md:w-auto flex flex-wrap gap-2">
            {/* UX-01 / UX-05 do Lote 1: cabeçalho reorganizado.
                - Primário: Registrar atividade (bg #008AB0/brand).
                - Secundário em contorno, cor só no ícone: Ganho / Perdido.
                - Menu ⋯ (DotsThree): Editar e Excluir oportunidade (em vermelho).
                Visão de gestor (gestao=true) permanece como estava:
                Reatribuir + Pedir retorno no WhatsApp.
                Lote 2 item 1: no celular, "Registrar atividade" ocupa 100% da
                largura; Ganho/Perdido dividem a largura que sobra igual, e o
                ⋯ fica 44×44. */}
            {!encerrada && !gestao && (
              <>
                {/* Desktop (md+): layout atual com "Registrar atividade" primário. */}
                <div className="hidden md:flex md:flex-row gap-2 w-full md:w-auto">
                  <button onClick={() => setRegistrarOpen({ deal, concluir: null })} data-testid="header-registrar-atividade"
                    className="flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover">
                    <Plus className="w-4 h-4" /> Registrar atividade
                  </button>
                  <div className="flex gap-2">
                    {(deal.stage === "Proposta" || deal.stage === "Negociação") && (
                      <button onClick={marcarGanho} data-testid="marcar-ganho-topo"
                        className="flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg border border-input bg-white text-sm font-medium text-navy hover:bg-slate-50">
                        <CheckCircle2 className="w-4 h-4 text-ok" /> Ganho
                      </button>
                    )}
                    <button onClick={abrirPerdido} data-testid="marcar-perdido-topo"
                      className="flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg border border-input bg-white text-sm font-medium text-navy hover:bg-slate-50">
                      <XCircle className="w-4 h-4 text-bad" /> Perdido
                    </button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid="deal-header-menu" aria-label="Mais ações"
                          className="flex-shrink-0 flex items-center justify-center h-9 w-9 rounded-lg border border-input bg-white text-slate-600 hover:bg-slate-50">
                          <MoreHorizontal className="w-4 h-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/deal/${id}/editar`)} data-testid="edit-deal-item">
                          <Pencil className="w-3.5 h-3.5 mr-2 text-slate-500" /> Editar
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={del} data-testid="delete-deal-item" className="text-bad focus:text-bad focus:bg-bad-soft">
                          <Trash2 className="w-3.5 h-3.5 mr-2" /> Excluir oportunidade
                        </DropdownMenuItem>
                        {isAdmin && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => { setAdminDelMotivo(""); setAdminDelOpen(true); }} data-testid="admin-delete-deal-item" className="text-bad focus:text-bad focus:bg-bad-soft">
                              <Trash2 className="w-3.5 h-3.5 mr-2" /> Excluir (Admin)
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {/* LOTE UX-1 A1 A2 — mobile: linha única [Mudar etapa] [Ganho]
                    [Perdido] [⋯]. "Registrar atividade" sai; o "+" da barra
                    inferior já abre a janela pré-preenchida pelo
                    CurrentDealContext. Ganho desabilitado fora de
                    Proposta/Negociação com explicação no title. */}
                <div className="flex md:hidden gap-2 w-full">
                  <button onClick={() => setMudarEtapaOpen(true)} data-testid="mobile-mudar-etapa"
                    className="flex-1 flex items-center justify-center gap-1.5 h-11 px-2 rounded-lg border border-input bg-white text-xs font-medium text-navy hover:bg-slate-50">
                    <GitBranch className="w-4 h-4 text-brand" /> Mudar etapa
                  </button>
                  <button
                    onClick={() => {
                      // LOTE UX-1 B4 (correção): title/tooltip não aparece em toque
                      // no celular. Mostramos a mensagem via toast quando desabilitado.
                      if (deal.stage === "Proposta" || deal.stage === "Negociação") marcarGanho();
                      else toast.info("Avance até Proposta ou Negociação para marcar como ganho");
                    }}
                    aria-disabled={!(deal.stage === "Proposta" || deal.stage === "Negociação")}
                    title={!(deal.stage === "Proposta" || deal.stage === "Negociação") ? "Avance até Proposta ou Negociação para marcar como ganho" : undefined}
                    data-testid="mobile-marcar-ganho"
                    className={`flex-1 flex items-center justify-center gap-1.5 h-11 px-2 rounded-lg border border-input bg-white text-xs font-medium text-navy hover:bg-slate-50 ${!(deal.stage === "Proposta" || deal.stage === "Negociação") ? "opacity-50" : ""}`}>
                    <CheckCircle2 className="w-4 h-4 text-ok" /> Ganho
                  </button>
                  <button onClick={abrirPerdido} data-testid="mobile-marcar-perdido"
                    className="flex-1 flex items-center justify-center gap-1.5 h-11 px-2 rounded-lg border border-input bg-white text-xs font-medium text-navy hover:bg-slate-50">
                    <XCircle className="w-4 h-4 text-bad" /> Perdido
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid="mobile-deal-menu" aria-label="Mais ações"
                        className="flex-shrink-0 flex items-center justify-center h-11 w-11 rounded-lg border border-input bg-white text-slate-600 hover:bg-slate-50">
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/deal/${id}/editar`)} data-testid="mobile-edit-deal-item">
                        <Pencil className="w-3.5 h-3.5 mr-2 text-slate-500" /> Editar
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={del} data-testid="mobile-delete-deal-item" className="text-bad focus:text-bad focus:bg-bad-soft">
                        <Trash2 className="w-3.5 h-3.5 mr-2" /> Excluir oportunidade
                      </DropdownMenuItem>
                      {isAdmin && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => { setAdminDelMotivo(""); setAdminDelOpen(true); }} data-testid="mobile-admin-delete-deal-item" className="text-bad focus:text-bad focus:bg-bad-soft">
                            <Trash2 className="w-3.5 h-3.5 mr-2" /> Excluir (Admin)
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </>
            )}
            {encerrada && !gestao && (
              <>
                {/* LOTE AJ-2 E1 — botão "Reabrir" aparece em oportunidades
                    Ganhas ou Perdidas. Backend restringe a dono/gestor/admin. */}
                <button
                  onClick={abrirReabrir}
                  data-testid="reabrir-deal-btn"
                  className="flex items-center justify-center gap-1.5 h-11 md:h-9 px-3 rounded-lg border border-input bg-white text-sm font-medium text-navy hover:bg-slate-50"
                  title={deal.stage === "Fechado" ? "Reabrir Ganho (não altera cliente)" : "Reabrir Perdido"}
                >
                  <RefreshCw className="w-4 h-4 text-brand" /> Reabrir
                </button>
                <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="deal-header-menu" aria-label="Mais ações"
                    className="flex items-center justify-center h-11 md:h-9 w-11 md:w-9 rounded-lg border border-input bg-white text-slate-600 hover:bg-slate-50">
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate(`/deal/${id}/editar`)} data-testid="edit-deal-item">
                    <Pencil className="w-3.5 h-3.5 mr-2 text-slate-500" /> Editar
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={del} data-testid="delete-deal-item" className="text-bad focus:text-bad focus:bg-bad-soft">
                    <Trash2 className="w-3.5 h-3.5 mr-2" /> Excluir oportunidade
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => { setAdminDelMotivo(""); setAdminDelOpen(true); }} data-testid="encerrada-admin-delete-deal-item" className="text-bad focus:text-bad focus:bg-bad-soft">
                        <Trash2 className="w-3.5 h-3.5 mr-2" /> Excluir (Admin)
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              </>
            )}
            {gestao && (
              <>
                <button
                  onClick={() => navigate(`/deal/${id}/editar`)}
                  data-testid="edit-deal-btn"
                  className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-input text-sm font-medium hover:bg-slate-50"
                  title="Reatribuir responsável"
                >
                  <Pencil className="w-3.5 h-3.5" /> Reatribuir
                </button>
                <CobrarWhatsApp
                  vendedor={{ id: deal.owner_id, name: deal.owner_name, phone: deal.owner_phone }}
                  cliente={deal.cliente_id ? { id: deal.cliente_id, nome: deal.cliente_nome } : null}
                  origem="equipe"
                  variant="primary"
                  testId={`deal-${deal.id}`}
                />
                {isAdmin && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid="gestao-admin-menu" aria-label="Ações admin"
                        className="flex-shrink-0 flex items-center justify-center h-11 md:h-9 w-11 md:w-9 rounded-lg border border-input bg-white text-slate-600 hover:bg-slate-50">
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => { setAdminDelMotivo(""); setAdminDelOpen(true); }} data-testid="gestao-admin-delete-deal-item" className="text-bad focus:text-bad focus:bg-bad-soft">
                        <Trash2 className="w-3.5 h-3.5 mr-2" /> Excluir (Admin)
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5">
          <div><div className="text-xs text-muted-foreground">Valor</div><div className="text-lg font-semibold text-navy" data-testid="deal-valor">{brl(deal.stage === "Fechado" && deal.valor_fechado ? deal.valor_fechado : deal.valor)}{deal.stage === "Fechado" && deal.valor_fechado && Number(deal.valor_fechado) !== Number(deal.valor) ? <span className="ml-2 text-xs text-muted-foreground font-normal">(previsto: {brl(deal.valor)})</span> : null}</div></div>
          <div><div className="text-xs text-muted-foreground">Probabilidade</div><div className="text-lg font-semibold text-navy">{deal.prob}%</div></div>
          <div><div className="text-xs text-muted-foreground">Previsão</div><div className="text-sm font-medium text-navy">{fmtDataPrevista(deal.data_prevista)}</div></div>
          <div><div className="text-xs text-muted-foreground">Responsável</div><div className="text-sm font-medium text-navy">{deal.owner_name || deal.responsavel || "—"}</div></div>
        </div>
      </div>

      {/* Stepper horizontal do funil — bolinhas CLICÁVEIS (F1).
          Encerrada (Fechado/Perdido) → sem clique. */}
      <div className="mt-4 bg-card rounded-2xl border border-black/5 p-4 md:p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] overflow-x-auto pipeline-scroll" data-testid="deal-stepper">
        <div className="flex items-center min-w-max gap-0">
          {stepperEtapas.map((s, i) => {
            const done = i < stepIndex;
            const active = i === stepIndex;
            const isPerdido = s === "Perdido";
            const isGanho = s === "Fechado";
            const clickable = !encerrada && isOwner && !active;
            const bg = active
              ? (isPerdido ? "bg-bad text-white" : isGanho ? "bg-ok text-white" : "bg-brand text-white")
              : done ? "bg-brand-soft text-brand-hover" : "bg-slate-100 text-slate-400";
            let labelStepper = s;
            if (isGanho) labelStepper = encerrada ? "Ganho" : "Encerramento";
            else if (isPerdido) labelStepper = encerrada ? "Perdido" : "Encerramento";
            return (
              <div key={s} className="flex items-center">
                <HelpTip label={stepperTipFinal(s, i)} maxLines={2}>
                <button
                  onClick={() => clickable && stepperClick(s)}
                  disabled={!clickable}
                  data-testid={`stepper-${s}`}
                  className={`flex flex-col items-center gap-1.5 px-3 py-1 ${clickable ? "cursor-pointer hover:brightness-95" : "cursor-default"}`}
                >
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${bg}`}>
                    {done ? <Check className="w-4 h-4" /> : i + 1}
                  </span>
                  <span className={`text-[11px] font-medium whitespace-nowrap ${active ? "text-navy" : done ? "text-brand-hover" : "text-slate-500"}`}>
                    {labelStepper}
                  </span>
                </button>
                </HelpTip>
                {i < stepperEtapas.length - 1 && (
                  <span className={`h-px w-8 md:w-12 mx-0.5 ${i < stepIndex ? "bg-brand" : "bg-slate-200"}`} />
                )}
              </div>
            );
          })}
        </div>
        {!encerrada && isOwner && (
          <div className="mt-3 text-xs text-muted-foreground text-center" data-testid="stepper-dica">
            Toque numa etapa para ir direto até ela.
          </div>
        )}
      </div>

      {/* PRÓXIMA AÇÃO — prioridade. Card inteiro clicável para o dono/gestor
          (A4 UX-1): com próxima ação → reagendar/concluir; sem próxima ação
          → abre Registrar atividade rolada até "Próximo passo". No gestor,
          mantém o modal de visualização. Em encerrada (Fechado/Perdido) o
          card com aviso "Sem próxima ação definida" NÃO aparece. */}
      {!encerrada && (
      <div
        onClick={() => {
          if (encerrada) return;
          if (gestao) { if (na) setGestorNaOpen(true); return; }
          if (isOwner) {
            if (na) abrirReagendar();
            else setRegistrarOpen({ deal, concluir: null, focusProx: true });
          }
        }}
        role={!encerrada && (gestao || isOwner) ? "button" : undefined}
        tabIndex={!encerrada && (gestao || isOwner) ? 0 : -1}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && !encerrada && (gestao || isOwner)) {
            e.preventDefault();
            if (gestao) { if (na) setGestorNaOpen(true); return; }
            if (na) abrirReagendar(); else setRegistrarOpen({ deal, concluir: null, focusProx: true });
          }
        }}
        className={`mt-4 rounded-2xl border p-5 transition-colors outline-none ${
          na ? (naOverdue ? "border-bad/30 bg-bad-soft/40" : "border-brand-accent bg-brand-soft")
             : "border-warn/30 bg-warn-soft/40"
        } ${!encerrada && (gestao || isOwner)
          ? "cursor-pointer hover:brightness-[0.98] focus:ring-2 focus:ring-brand/40"
          : "cursor-default"}`}
        data-testid="proxima-acao"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${na ? (naOverdue ? "bg-bad text-white" : "bg-brand text-white") : "bg-warn text-white"}`}>
              {na ? <Clock className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Próxima Ação</div>
              {na ? (
                <div className="text-sm font-semibold text-navy">{na.tipo}{na.titulo && na.titulo !== na.tipo ? ` · ${na.titulo}` : ""} <span className={`font-normal ${naOverdue ? "text-bad" : "text-muted-foreground"}`}>· {fmtDateTime(na.data)}{naOverdue ? " (atrasada)" : ""}</span></div>
              ) : (
                <div className="text-sm font-semibold text-warn">Sem próxima ação definida</div>
              )}
            </div>
          </div>
        </div>
        {!encerrada && !gestao && na && (
          <div className="flex flex-wrap gap-2 mt-4">
            <button
              onClick={(e) => { e.stopPropagation(); setRegistrarOpen({ deal, concluir: na }); }}
              data-testid="concluir-proxima-acao"
              className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-white border border-black/5 text-sm font-medium text-navy hover:bg-slate-50"
            >
              <CheckCircle2 className="w-4 h-4 text-ok" /> Concluir
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); abrirReagendar(); }}
              data-testid="reagendar-proxima-acao"
              className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-white border border-black/5 text-sm font-medium text-navy hover:bg-slate-50"
            >
              <Clock className="w-4 h-4 text-brand" /> Reagendar
            </button>
          </div>
        )}
      </div>
      )}

      {/* Cobranças pendentes para o responsável responder */}
      {isOwner && cobrancasAbertas.length > 0 ? (
        <div className="mt-4 rounded-2xl border border-warn/30 bg-warn-soft/40 p-4" data-testid="cobrancas-pendentes">
          <div className="flex items-center gap-2 text-warn mb-2"><Megaphone className="w-4 h-4" /><span className="text-xs font-semibold uppercase tracking-wider">Solicitações abertas</span></div>
          <div className="space-y-2">
            {cobrancasAbertas.map((c) => (
              <div key={c.id} className="bg-white rounded-lg p-3 border border-black/5">
                <div className="text-xs text-muted-foreground mb-1">{c.author_name} · {fmtDateTime(c.created_at)}</div>
                <div className="text-sm text-navy">{c.text}</div>
                <button onClick={() => { setRespCobrId(c.id); setRespText(""); }} data-testid={`responder-cobranca-${c.id}`} className="mt-2 text-xs font-semibold text-brand hover:underline">Responder</button>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* PARA AVANÇAR FALTA — item 3 do PEDIDO_EMERGENT_2B.
          A lista clicável "Etapa do funil" saiu. Só bolinhas (não clicam),
          este quadro, e os botões Ganho/Perdido/Voltar etapa. */}
      {!encerrada && !gestao ? (
        <div className="mt-4 bg-card rounded-2xl border border-black/5 p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
          {proximo?.proxima && (
            <div className="mb-4" data-testid="para-avancar-falta">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Próximo passo: <span className="text-navy">{proximo.proxima}</span>
              </div>
              <ul className="mt-3 space-y-2">
                {(proximo.itens || []).map((it) => (
                  <li key={it.chave} className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm ${it.ok ? "border-ok/30 bg-ok-soft/40" : "border-warn/30 bg-warn-soft/40"}`} data-testid={`avancar-item-${it.chave}`}>
                    <span className="flex items-center gap-2 min-w-0 flex-1">
                      {it.ok ? <CheckCircle2 className="w-4 h-4 text-ok shrink-0" /> : <XCircle className="w-4 h-4 text-warn shrink-0" />}
                      <span className={`truncate ${it.ok ? "text-navy" : "text-navy"}`}>{it.texto}</span>
                    </span>
                    {!it.ok && (
                      <button
                        type="button"
                        onClick={() => {
                          if (it.acao === "registrar_atividade") setRegistrarOpen({ deal, concluir: null, oqInicial: it.tipo_atividade });
                          else if (it.acao === "editar_cliente" && deal.cliente_id) navigate(`/cliente/${deal.cliente_id}/editar`);
                          else if (it.acao === "editar_deal") navigate(`/deal/${id}/editar`);
                        }}
                        className="text-xs font-semibold text-brand hover:underline shrink-0"
                        data-testid={`avancar-acao-${it.chave}`}
                      >
                        {it.acao === "registrar_atividade" ? "Registrar atividade" : "Informar"}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : !encerrada ? null : (
        <div className={`mt-4 rounded-2xl border p-5 flex items-center gap-3 ${deal.stage === "Fechado" ? "border-ok/30 bg-ok-soft" : "border-bad/30 bg-bad-soft"}`}>
          {deal.stage === "Fechado" ? <CheckCircle2 className="w-6 h-6 text-ok" /> : <XCircle className="w-6 h-6 text-bad" />}
          <div className="flex-1">
            <div className={`font-semibold ${deal.stage === "Fechado" ? "text-ok" : "text-bad"}`}>{deal.stage === "Fechado" ? "Oportunidade Ganha" : "Oportunidade Perdida"}</div>
            {deal.motivo_perda && <div className="text-sm text-bad/80">Motivo: {deal.motivo_perda}</div>}
          </div>
        </div>
      )}

      {/* LOTE F1 — bloco "Orientação Allia" foi retirado da página da
          oportunidade. O campo `fase_mandala` continua no banco e a aba
          "Orientações" do cliente segue inalterada. */}

      {/* Registros */}
      <Tabs defaultValue="comentarios" className="mt-6">
        <TabsList>
          <TabsTrigger value="comentarios" data-testid="tab-comentarios">Comentários ({comments.length})</TabsTrigger>
          <TabsTrigger value="historico" data-testid="tab-historico">Histórico ({history?.count || 0})</TabsTrigger>
          <TabsTrigger value="fotos" data-testid="tab-fotos">Fotos ({fotos.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="comentarios" className="mt-4">
          <form onSubmit={addComment} className="flex gap-2 mb-4">
            <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Escreva um comentário..." data-testid="comment-input" className="flex-1 h-11 px-3.5 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40" />
            <button type="submit" data-testid="comment-submit" className="h-11 px-4 rounded-lg bg-brand text-white hover:bg-brand-hover flex items-center gap-1.5 text-sm font-medium"><Send className="w-4 h-4" /></button>
          </form>
          <div className="space-y-3">
            {comments.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Nenhum comentário ainda.</p>}
            {comments.map((c) => (
              <div key={c.id} className="bg-card rounded-xl border border-black/5 p-4 flex gap-3">
                <Avatar name={c.author_name} size={34} />
                <div className="flex-1"><div className="flex items-center gap-2"><span className="font-medium text-navy text-sm">{c.author_name}</span><span className="text-xs text-muted-foreground">{fmtDateTime(c.created_at)}</span></div><p className="text-sm text-slate-600 mt-1">{c.text}</p>
                  <div className="mt-1.5"><LikeButton entityType="comentario" entityId={c.id} authorId={c.author_id} dealId={id} label={c.text?.slice(0, 60)} count={c.likes_count || 0} liked={!!c.liked} /></div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="historico" className="mt-4">
          <div className="relative pl-6 space-y-4">
            <div className="absolute left-[9px] top-2 bottom-2 w-px bg-slate-200" />
            {(history?.events || []).map((ev, i) => {
              const Icon = EVENT_ICON[ev.type] || Clock;
              return (
                <div key={i} className="relative">
                  <div className="absolute -left-6 top-0.5 w-[18px] h-[18px] rounded-full bg-brand-soft border-2 border-white flex items-center justify-center"><Icon className="w-2.5 h-2.5 text-brand" /></div>
                  <div className="text-sm font-medium text-navy">{ev.title}</div>
                  {ev.detail && <div className="text-xs text-slate-600">{ev.detail}</div>}
                  <div className="text-xs text-muted-foreground mt-0.5">{ev.actor} · {fmtDateTime(ev.at)}</div>
                </div>
              );
            })}
          </div>
        </TabsContent>
        <TabsContent value="fotos" className="mt-4">
          <label className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-brand text-white text-sm font-medium cursor-pointer hover:bg-brand-hover mb-4" data-testid="upload-foto-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Anexar foto
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={upload} disabled={uploading} />
          </label>
          {fotos.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">Nenhuma foto anexada.</p> : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {fotos.map((f) => <div key={f.id} className="rounded-xl overflow-hidden border border-black/5 bg-slate-100 aspect-square"><img src={fileUrl(f.id)} alt={f.categoria || "foto"} className="w-full h-full object-cover" loading="lazy" /></div>)}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog atividade */}
      <Dialog open={ativOpen} onOpenChange={setAtivOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{ativForm.tipo === "Próximo contato" ? "Agendar Próxima Ação" : `Registrar ${ativForm.tipo}`}</DialogTitle></DialogHeader>
          <form onSubmit={salvarAtiv} className="space-y-3" data-testid="deal-ativ-form">
            <select value={ativForm.tipo} onChange={(e) => setAtivForm((f) => ({ ...f, tipo: e.target.value }))} className={INPUT} data-testid="deal-ativ-tipo">
              {["Próximo contato", "Ligação", "Reunião", "Visita", "Email", "Tarefa", "Retorno"].map((t) => <option key={t}>{t}</option>)}
            </select>
            <input placeholder="Descrição (opcional)" value={ativForm.titulo} onChange={(e) => setAtivForm((f) => ({ ...f, titulo: e.target.value }))} className={INPUT} data-testid="deal-ativ-titulo" />
            <div>
              <label className="text-xs font-medium text-slate-600">Data e hora</label>
              <input type="datetime-local" value={ativForm.data} onChange={(e) => setAtivForm((f) => ({ ...f, data: e.target.value }))} className={INPUT} data-testid="deal-ativ-data" />
            </div>
            <DialogFooter>
              <button type="submit" disabled={savingAtiv} data-testid="deal-ativ-save" className="h-10 px-5 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover flex items-center gap-2 disabled:opacity-60">
                {savingAtiv && <Loader2 className="w-4 h-4 animate-spin" />} Salvar
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reagendar próxima ação — componente compartilhado */}
      <ReagendarProximaDialog
        open={reagOpen}
        onClose={() => setReagOpen(false)}
        value={reagData}
        onChange={setReagData}
        onSalvar={reagendarProximaAcao}
        testId="deal-reag"
      />

      {/* Divergência de data no Reagendar — componente compartilhado */}
      <DivergenciaDataDialog
        open={!!reagDiverg}
        onClose={() => setReagDiverg(null)}
        dataTexto={reagDiverg?.texto}
        dataCampo={reagDiverg?.campo}
        testId="reag-div-dialog"
        onManter={() => { const c = reagDiverg.campo; setReagDiverg(null); _executarReagendar(c); }}
        onUsarTexto={() => {
          const base = reagDiverg.campo;
          const nova = new Date(
            reagDiverg.texto.getFullYear(), reagDiverg.texto.getMonth(), reagDiverg.texto.getDate(),
            base.getHours(), base.getMinutes(), 0, 0
          );
          setReagDiverg(null);
          _executarReagendar(nova);
        }}
      />

      {/* Detalhes da próxima ação para o gestor (read-only + Pedir retorno) */}
      <Dialog open={gestorNaOpen} onOpenChange={setGestorNaOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Próxima ação (visualização)</DialogTitle></DialogHeader>
          {na ? (
            <div className="space-y-3 text-sm" data-testid="gestor-na-detalhes">
              <div><span className="text-xs uppercase tracking-wider text-muted-foreground">Tipo</span><div className="font-medium text-navy">{na.tipo}</div></div>
              <div><span className="text-xs uppercase tracking-wider text-muted-foreground">Data e hora</span><div className={`font-medium ${naOverdue ? "text-bad" : "text-navy"}`}>{fmtDateTime(na.data)}{naOverdue ? " (atrasada)" : ""}</div></div>
              {na.titulo && na.titulo !== na.tipo && (
                <div><span className="text-xs uppercase tracking-wider text-muted-foreground">Título</span><div className="font-medium text-navy">{na.titulo}</div></div>
              )}
              {na.descricao && (
                <div><span className="text-xs uppercase tracking-wider text-muted-foreground">Descrição</span><div className="whitespace-pre-wrap text-navy">{na.descricao}</div></div>
              )}
              <div><span className="text-xs uppercase tracking-wider text-muted-foreground">Responsável</span><div className="font-medium text-navy">{deal?.owner_name || "—"}</div></div>
              {na.criada_em && (
                <div><span className="text-xs uppercase tracking-wider text-muted-foreground">Criada em</span><div className="text-slate-600">{fmtDateTime(na.criada_em)}</div></div>
              )}
              <p className="text-xs text-muted-foreground italic pt-2 border-t">Apenas o vendedor responsável conclui ou reagenda a atividade.</p>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">Sem próxima ação.</div>
          )}
          <DialogFooter>
            {deal?.owner_id && (
              <CobrarWhatsApp
                vendedor={{ id: deal.owner_id, name: deal.owner_name, phone: deal.owner_phone }}
                cliente={deal.cliente_id ? { id: deal.cliente_id, nome: deal.cliente_nome } : null}
                origem="equipe"
                variant="primary"
                testId={`gestor-na-${deal.id}`}
              />
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Marcar Perdido — motivo obrigatório */}
      <Dialog open={perdaOpen} onOpenChange={setPerdaOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Marcar como Perdido</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Motivo da perda *</label>
              <select value={perdaMotivo} onChange={(e) => setPerdaMotivo(e.target.value)} className={INPUT} data-testid="motivo-perda-select">
                <option value="">Selecione o motivo…</option>
                {MOTIVOS_PERDA.map((m) => <option key={m}>{m}</option>)}
              </select>
            </div>
            {perdaMotivo === "Outro" ? (
              <div>
                <label className="text-xs font-medium text-slate-600">Descreva *</label>
                <input value={perdaOutro} onChange={(e) => setPerdaOutro(e.target.value)} className={INPUT} placeholder="Motivo específico" data-testid="motivo-perda-outro" />
              </div>
            ) : null}
            {/* LOTE AJ-2 E2 — janela do motivo da perda com retorno automático. */}
            <div className="pt-2 border-t border-slate-100">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={perdaNaoCriarRetorno}
                  onChange={(e) => setPerdaNaoCriarRetorno(e.target.checked)}
                  data-testid="perda-nao-criar-retorno"
                  className="w-4 h-4 accent-brand"
                />
                Não criar retorno
              </label>
              {!perdaNaoCriarRetorno && (
                <div className="mt-2 flex items-center gap-2">
                  <label className="text-xs font-medium text-slate-600">Retorno em</label>
                  <input
                    type="number"
                    min={1}
                    value={perdaDiasRetorno}
                    onChange={(e) => setPerdaDiasRetorno(e.target.value)}
                    data-testid="perda-dias-retorno"
                    className="w-20 h-9 px-2 rounded-lg border border-input text-sm text-center"
                  />
                  <span className="text-sm text-slate-600">dias</span>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <button onClick={marcarPerdido} disabled={!perdaMotivo || (perdaMotivo === "Outro" && !perdaOutro.trim())} data-testid="motivo-perda-salvar" className="h-10 px-5 rounded-lg bg-bad text-white text-sm font-semibold hover:bg-bad/90 disabled:opacity-60">Confirmar perda</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* LOTE AJ-2 E1 — Reabrir Ganho/Perdido */}
      <Dialog open={reabrirOpen} onOpenChange={setReabrirOpen}>
        <DialogContent data-testid="reabrir-deal-dialog">
          <DialogHeader>
            <DialogTitle>Reabrir oportunidade</DialogTitle>
            <DialogDescription>
              A oportunidade volta para a etapa anterior ao encerramento.
              Descreva o motivo da reabertura — fica no histórico.
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={reabrirMotivo}
            onChange={(e) => setReabrirMotivo(e.target.value)}
            rows={3}
            placeholder="Ex.: cliente retornou interesse."
            data-testid="reabrir-motivo"
            className={INPUT + " h-auto py-2.5"}
          />
          <DialogFooter>
            <button
              onClick={reabrirDeal}
              disabled={reabrirMotivo.trim().length < 3}
              data-testid="reabrir-confirmar"
              className="h-10 px-5 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover disabled:opacity-60"
            >
              Confirmar reabertura
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pedir retorno movido para o componente CobrarWhatsApp (modal unificado) */}

      {/* Responder solicitação */}
      <Dialog open={!!respCobrId} onOpenChange={(o) => !o && setRespCobrId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Responder solicitação</DialogTitle></DialogHeader>
          <textarea rows={4} value={respText} onChange={(e) => setRespText(e.target.value)} className={INPUT + " h-auto py-2.5"} placeholder="Sua resposta ao gestor" data-testid="responder-cobranca-texto" />
          <DialogFooter>
            <button onClick={responderCobranca} disabled={!respText.trim()} data-testid="responder-cobranca-enviar" className="h-10 px-5 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover disabled:opacity-60">Responder</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RegistrarAtividade
        open={!!registrarOpen}
        onOpenChange={(o) => { if (!o) setRegistrarOpen(null); }}
        cliente={deal ? { id: deal.cliente_id, nome: deal.cliente_nome, empresa: deal.cliente_nome } : null}
        deal={registrarOpen?.deal || deal}
        concluir={registrarOpen?.concluir || null}
        focusProx={!!registrarOpen?.focusProx}
        oqInicial={registrarOpen?.oqInicial}
        onSaved={load}
      />

      {/* LOTE F1 — modal "Para ir até <alvo> falta:" quando bolinha à frente
          clicada e nem todos os requisitos das etapas intermediárias cumpridos. */}
      <Dialog open={!!faltaOpen} onOpenChange={(o) => { if (!o) setFaltaOpen(null); }}>
        <DialogContent data-testid="falta-dialog">
          <DialogHeader>
            <DialogTitle>Para ir até {faltaOpen?.alvo} falta:</DialogTitle>
          </DialogHeader>
          <ul className="space-y-2 mt-2">
            {(faltaOpen?.itens || []).map((it) => (
              <li key={`${it.etapa}-${it.chave}`} className="flex items-center justify-between gap-3 rounded-lg border border-warn/30 bg-warn-soft/40 px-3 py-2 text-sm" data-testid={`falta-item-${it.etapa}-${it.chave}`}>
                <span className="flex items-center gap-2 min-w-0 flex-1">
                  <XCircle className="w-4 h-4 text-warn shrink-0" />
                  <span className="truncate text-navy">{it.texto}<span className="text-muted-foreground"> · {it.etapa}</span></span>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setFaltaOpen(null);
                    if (it.acao === "registrar_atividade") setRegistrarOpen({ deal, concluir: null, oqInicial: it.tipo_atividade });
                    else if (it.acao === "editar_cliente" && deal.cliente_id) navigate(`/cliente/${deal.cliente_id}/editar`);
                    else if (it.acao === "editar_deal") navigate(`/deal/${id}/editar`);
                  }}
                  className="text-xs font-semibold text-brand hover:underline shrink-0"
                  data-testid={`falta-acao-${it.etapa}-${it.chave}`}
                >
                  {it.acao === "registrar_atividade" ? "Registrar atividade" : "Informar"}
                </button>
              </li>
            ))}
          </ul>
          <DialogFooter>
            <button type="button" onClick={() => setFaltaOpen(null)} className="h-10 px-4 rounded-lg border border-input text-sm font-medium hover:bg-slate-50">Fechar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={voltarOpen} onOpenChange={setVoltarOpen}>
        <DialogContent data-testid="voltar-etapa-dialog">
          <DialogHeader>
            <DialogTitle>Voltar de etapa</DialogTitle>
            <DialogDescription>
              Descreva por que a oportunidade está retrocedendo. Fica no histórico.
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={voltarMotivo}
            onChange={(e) => setVoltarMotivo(e.target.value)}
            rows={3}
            placeholder="Ex.: cliente pediu para refazer teste."
            className="w-full px-3.5 py-2.5 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40"
            data-testid="voltar-etapa-motivo"
            minLength={3}
            required
          />
          <DialogFooter>
            <button
              type="button"
              onClick={() => setVoltarOpen(false)}
              className="h-10 px-4 rounded-lg border border-input text-sm hover:bg-slate-50"
            >Cancelar</button>
            <button
              type="button"
              onClick={voltarEtapa}
              disabled={voltarBusy || voltarMotivo.trim().length < 3}
              data-testid="voltar-etapa-salvar"
              className="h-10 px-5 rounded-lg bg-navy text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-2"
            >
              {voltarBusy && <Loader2 className="w-4 h-4 animate-spin" />} Confirmar volta
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pularOpen} onOpenChange={setPularOpen}>
        <DialogContent data-testid="pular-etapa-dialog">
          <DialogHeader>
            <DialogTitle>Avançar etapa</DialogTitle>
            <DialogDescription>
              Escolha a etapa de destino e descreva o motivo. Fica no histórico.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Etapa de destino</label>
              <select
                value={pularStage}
                onChange={(e) => setPularStage(e.target.value)}
                data-testid="pular-etapa-destino"
                className="mt-1 w-full h-11 px-3 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40"
              >
                <option value="">— Selecione —</option>
                {(() => {
                  const idx = OPEN_STAGES.indexOf(deal?.stage || "");
                  return OPEN_STAGES.slice(idx + 2).map((s) => <option key={s} value={s}>{s}</option>);
                })()}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Motivo</label>
              <textarea
                value={pularMotivo}
                onChange={(e) => setPularMotivo(e.target.value)}
                rows={3}
                placeholder='Ex.: "cliente não precisou de teste."'
                className="mt-1 w-full px-3.5 py-2.5 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40"
                data-testid="pular-etapa-motivo"
                minLength={3}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setPularOpen(false)}
              className="h-10 px-4 rounded-lg border border-input text-sm hover:bg-slate-50"
            >Cancelar</button>
            <button
              type="button"
              onClick={pularEtapa}
              disabled={pularBusy || pularMotivo.trim().length < 3 || !pularStage}
              data-testid="pular-etapa-salvar"
              className="h-10 px-5 rounded-lg bg-navy text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-2"
            >
              {pularBusy && <Loader2 className="w-4 h-4 animate-spin" />} Confirmar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* LOTE UX-1 A3 — "Mudar etapa" (mobile). Lista as etapas e reaproveita
          os diálogos de Voltar (regra atual) e Avançar (regra atual, antes
          chamado "Pular etapa"). Não cria regra nova. */}
      <Dialog open={mudarEtapaOpen} onOpenChange={setMudarEtapaOpen}>
        <DialogContent data-testid="mudar-etapa-dialog">
          <DialogHeader>
            <DialogTitle>Mudar etapa</DialogTitle>
            <DialogDescription>Toque na etapa desejada. Voltar e avançar seguem as regras atuais.</DialogDescription>
          </DialogHeader>
          <ul className="space-y-2">
            {OPEN_STAGES.map((s) => {
              const curIdx = OPEN_STAGES.indexOf(deal?.stage || "");
              const sIdx = OPEN_STAGES.indexOf(s);
              const isAtual = s === deal?.stage;
              const isAnterior = sIdx === curIdx - 1;
              const isFuturo = sIdx > curIdx;
              const clickable = isAnterior || isFuturo;
              return (
                <li key={s}>
                  <button
                    type="button"
                    disabled={!clickable}
                    data-testid={`mudar-etapa-${s}`}
                    onClick={() => {
                      setMudarEtapaOpen(false);
                      if (isAnterior) { setVoltarMotivo(""); setVoltarOpen(true); }
                      else if (isFuturo) { setPularStage(s); setPularMotivo(""); setPularOpen(true); }
                    }}
                    className={`w-full text-left flex items-center justify-between gap-3 px-4 py-3 rounded-lg border ${isAtual ? "border-brand/40 bg-brand-soft/40" : "border-input bg-white"} ${clickable ? "hover:bg-slate-50" : "opacity-60 cursor-default"}`}
                  >
                    <span className="flex items-center gap-3">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${sIdx < curIdx ? "bg-brand-soft text-brand-hover" : isAtual ? "bg-brand text-white" : "bg-slate-100 text-slate-500"}`}>
                        {sIdx < curIdx ? <Check className="w-3.5 h-3.5" /> : sIdx + 1}
                      </span>
                      <span className="text-sm font-medium text-navy">{s}</span>
                    </span>
                    {isAtual && <span className="text-[11px] font-semibold text-brand">Atual</span>}
                    {isAnterior && <span className="text-[11px] font-semibold text-slate-500">Voltar aqui</span>}
                    {isFuturo && <span className="text-[11px] font-semibold text-brand">Avançar aqui</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </DialogContent>
      </Dialog>

      {/* LOTE LT-1b — modal admin: motivo obrigatório, vale em qualquer etapa. */}
      <Dialog open={adminDelOpen} onOpenChange={setAdminDelOpen}>
        <DialogContent data-testid="admin-delete-dialog">
          <DialogHeader>
            <DialogTitle>Excluir oportunidade (Admin)</DialogTitle>
            <DialogDescription>
              A oportunidade e tudo ligado a ela (atividades, comentários, fotos, histórico)
              vão para a lixeira. É reversível pelo admin.
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Motivo</label>
            <textarea
              value={adminDelMotivo}
              onChange={(e) => setAdminDelMotivo(e.target.value)}
              rows={3}
              minLength={3}
              required
              placeholder="Ex.: Registro duplicado criado por teste."
              data-testid="admin-delete-motivo"
              className="mt-1 w-full px-3.5 py-2.5 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40"
            />
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setAdminDelOpen(false)}
              className="h-10 px-4 rounded-lg border border-input text-sm hover:bg-slate-50">Cancelar</button>
            <button
              type="button"
              onClick={adminDel}
              disabled={adminDelBusy || adminDelMotivo.trim().length < 3}
              data-testid="admin-delete-confirm"
              className="h-10 px-5 rounded-lg bg-bad text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-2"
            >
              {adminDelBusy && <Loader2 className="w-4 h-4 animate-spin" />} Excluir
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* LOTE G1 — "Fechar como GANHO" + celebração. */}
      <GanhoModal
        open={ganhoOpen}
        busy={ganhoBusy}
        clienteNome={deal?.cliente_nome}
        valorPrevisto={deal?.valor}
        onClose={() => !ganhoBusy && setGanhoOpen(false)}
        onConfirm={confirmarGanho}
      />
      <CelebrationOverlay
        open={celebrateOpen}
        clienteNome={celebrateInfo?.clienteNome}
        valorFechado={celebrateInfo?.valorFechado}
        primeiroNome={celebrateInfo?.primeiroNome}
        onClose={() => setCelebrateOpen(false)}
      />
    </div>
  );
}
